/**
 * Rate limiting utility for Google Places API to prevent cost overruns
 *
 * Implements multiple layers of protection:
 * - Per-minute rate limiting (default: 10 requests/minute)
 * - Daily session quota (default: 100 requests/day)
 * - Request tracking via sessionStorage for persistence across page navigations
 */

const STORAGE_KEY = 'places_api_usage'
const REQUESTS_PER_MINUTE = 30 // Increased from 10 to allow more active searching
const REQUESTS_PER_DAY = 500 // Increased from 100 to allow more daily usage
const MIN_SEARCH_LENGTH = 3

interface UsageData {
	minuteRequests: number[]
	dailyCount: number
	dailyResetTime: number
}

function getUsageData(): UsageData {
	if (typeof window === 'undefined') {
		return { minuteRequests: [], dailyCount: 0, dailyResetTime: Date.now() }
	}

	try {
		const stored = sessionStorage.getItem(STORAGE_KEY)
		if (stored) {
			const data = JSON.parse(stored) as UsageData
			// Reset daily count if it's a new day
			if (Date.now() > data.dailyResetTime) {
				return {
					minuteRequests: [],
					dailyCount: 0,
					dailyResetTime: getEndOfDay(),
				}
			}
			return data
		}
	} catch {
		// Ignore parse errors
	}

	return {
		minuteRequests: [],
		dailyCount: 0,
		dailyResetTime: getEndOfDay(),
	}
}

function saveUsageData(data: UsageData): void {
	if (typeof window === 'undefined') return
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
	} catch {
		// Ignore storage errors
	}
}

function getEndOfDay(): number {
	const now = new Date()
	const endOfDay = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate() + 1,
		0,
		0,
		0,
	)
	return endOfDay.getTime()
}

function cleanOldRequests(requests: number[]): number[] {
	const oneMinuteAgo = Date.now() - 60 * 1000
	return requests.filter(timestamp => timestamp > oneMinuteAgo)
}

export interface RateLimitResult {
	allowed: boolean
	reason?: 'rate_limit' | 'daily_quota' | 'min_length'
	remainingMinute: number
	remainingDaily: number
}

/**
 * Check if a Places API request is allowed under rate limits
 */
export function checkPlacesRateLimit(searchValue: string): RateLimitResult {
	// Check minimum length requirement
	if (searchValue.trim().length < MIN_SEARCH_LENGTH) {
		return {
			allowed: false,
			reason: 'min_length',
			remainingMinute: REQUESTS_PER_MINUTE,
			remainingDaily: REQUESTS_PER_DAY,
		}
	}

	const data = getUsageData()

	// Clean old minute requests
	data.minuteRequests = cleanOldRequests(data.minuteRequests)

	// Check daily quota
	if (data.dailyCount >= REQUESTS_PER_DAY) {
		return {
			allowed: false,
			reason: 'daily_quota',
			remainingMinute: Math.max(
				0,
				REQUESTS_PER_MINUTE - data.minuteRequests.length,
			),
			remainingDaily: 0,
		}
	}

	// Check per-minute rate limit
	if (data.minuteRequests.length >= REQUESTS_PER_MINUTE) {
		return {
			allowed: false,
			reason: 'rate_limit',
			remainingMinute: 0,
			remainingDaily: Math.max(0, REQUESTS_PER_DAY - data.dailyCount),
		}
	}

	return {
		allowed: true,
		remainingMinute: REQUESTS_PER_MINUTE - data.minuteRequests.length - 1,
		remainingDaily: REQUESTS_PER_DAY - data.dailyCount - 1,
	}
}

/**
 * Record a Places API request for rate limiting tracking
 */
export function recordPlacesRequest(): void {
	const data = getUsageData()
	data.minuteRequests = cleanOldRequests(data.minuteRequests)
	data.minuteRequests.push(Date.now())
	data.dailyCount++
	saveUsageData(data)
}

/**
 * Get current rate limit status without recording a request
 */
export function getPlacesRateLimitStatus(): {
	minuteUsed: number
	dailyUsed: number
	minuteLimit: number
	dailyLimit: number
} {
	const data = getUsageData()
	const minuteRequests = cleanOldRequests(data.minuteRequests)

	return {
		minuteUsed: minuteRequests.length,
		dailyUsed: data.dailyCount,
		minuteLimit: REQUESTS_PER_MINUTE,
		dailyLimit: REQUESTS_PER_DAY,
	}
}

/**
 * Clear/reset all rate limit data (useful for testing or if limits are hit)
 */
export function clearPlacesRateLimit(): void {
	if (typeof window === 'undefined') return
	try {
		sessionStorage.removeItem(STORAGE_KEY)
	} catch {
		// Ignore storage errors
	}
}

export const PLACES_RATE_LIMITS = {
	REQUESTS_PER_MINUTE,
	REQUESTS_PER_DAY,
	MIN_SEARCH_LENGTH,
} as const
